import store from "../core/store";
import { Format } from "../core/helpers/formatHelper";

type T = (key: string) => string;

const ByDays = 0;
const Booking = 0;
const Product = 1;
const InProgress = 2;

// What the customer zone says about a program, built from the data of the API so every screen says the same.
// CodeScanner registration of the keys used through t():
// $t("1 week").toString() $t("{0} weeks").toString() $t("1 day").toString() $t("{0} days").toString() $t("1 hour").toString() $t("{0} hours").toString() $t("1 phase").toString() $t("{0} phases").toString()
// $t("from {0}").toString() $t("or {0} installments").toString() $t("or {0} installments of {1}").toString() $t("or {0} installments from {1}").toString()
// $t("Cancel up to {0} before the first appointment and get the unused part refunded").toString() $t("Cancel up to {0} before the first appointment and keep the unused part as balance").toString()
// $t("Starts on {0}").toString() $t("{0} days to complete it").toString() $t("{0} later").toString() $t("Starts at {0}").toString() $t("Starts between {0} and {1}").toString()
// $t("At the start").toString() $t("Between {0} and {1} later").toString() $t("At least {0} later").toString() $t("min") $t("1 per week").toString() $t("every {0} days").toString()
// $t("or {0} min").toString() $t("{0} and {1} more").toString() $t("1 appointment").toString() $t("{0} appointments").toString() $t("in one day").toString() $t("in {0} days").toString()
// $t("1 item").toString() $t("{0} items").toString() $t("In progress").toString() $t("Finished").toString() $t("Cancelled").toString() $t("Expired").toString() $t("Since {0}").toString()
// $t("You have until {0}").toString() $t("{0} of {1} appointments done").toString() $t("cancelled").toString() $t("done").toString() $t("booked").toString() $t("not booked").toString()
// $t("bookable from {0}").toString() $t("after the previous appointments").toString() $t("Book").toString() $t("Cancel")
export class ProgramPackTexts {
  static culture(): string {
    const c = store.state.settings ? store.state.settings.Culture : "";
    return c ? c : "en-US";
  }

  static money(value: number): string {
    return Format.formatCurrency(Number(value).toFixed(2));
  }

  static count(t: T, n: number, one: string, many: string): string {
    return n === 1 ? t(one) : t(many).replace("{0}", String(n));
  }

  static days(t: T, n: number): string {
    return n % 7 === 0 && n >= 7
      ? this.count(t, n / 7, "1 week", "{0} weeks")
      : this.count(t, n, "1 day", "{0} days");
  }

  static hour(minutes: number): string {
    return Math.floor(minutes / 60) + ":" + ("0" + (minutes % 60)).slice(-2);
  }

  static date(input: string, withYear: boolean = true): string {
    const options: any = { day: "numeric", month: "short" };
    if (withYear) options.year = "numeric";
    return new Intl.DateTimeFormat(this.culture(), options).format(new Date(input));
  }

  static dateTime(input: string): string {
    const time = new Intl.DateTimeFormat(this.culture(), { hour: "2-digit", minute: "2-digit" }).format(new Date(input));
    return `${this.date(input, false)} · ${time}`;
  }

  static media(p: any): any {
    const folder = store.state.settings.Uploads + "/ProgramPack/";
    return {
      images: (p.images || []).map((name: string) => folder + "Gallery/" + name),
      videos: p.videos || [],
      videoFirst: p.videoFirst === true,
    };
  }

  static hasMedia(p: any): boolean {
    return (p.images || []).length > 0 || (p.videos || []).length > 0;
  }

  static tag(t: T, p: any): string {
    const n = p.columns.length;
    return p.layout === ByDays ? this.count(t, n, "1 day", "{0} days") : this.count(t, n, "1 phase", "{0} phases");
  }

  static price(t: T, p: any): string {
    return p.priceFrom === p.priceTo ? this.money(p.priceFrom) : t("from {0}").replace("{0}", this.money(p.priceFrom));
  }

  static installments(t: T, p: any, withAmount: boolean): string {
    if (!p.installmentCount) return "";
    if (!withAmount) return t("or {0} installments").replace("{0}", p.installmentCount);
    if (p.priceFrom === p.priceTo && p.firstInstallmentAmount !== p.installmentAmount) {
      return t("or {0} installments of {1} (the first one {2})")
        .replace("{0}", p.installmentCount).replace("{1}", this.money(p.installmentAmount)).replace("{2}", this.money(p.firstInstallmentAmount));
    }
    const key = p.priceFrom === p.priceTo ? "or {0} installments of {1}" : "or {0} installments from {1}";
    return t(key).replace("{0}", p.installmentCount).replace("{1}", this.money(p.installmentAmount));
  }

  static cancellation(t: T, p: any): string {
    if (p.cancellationPolicy !== 1 || !p.cancelUntilValue) return "";
    const until = p.cancelUntilUnit === 0
      ? this.count(t, p.cancelUntilValue, "1 hour", "{0} hours")
      : this.count(t, p.cancelUntilValue, "1 day", "{0} days");
    return t("Cancel up to {0} before the first appointment and get the unused part refunded").replace("{0}", until);
  }

  static startDays(t: T, p: any): string {
    const checked = (p.startDays || []).filter((d: boolean) => d).length;
    if (p.layout !== ByDays || checked === 0 || checked === 7) return "";
    const weekday = new Intl.DateTimeFormat(this.culture(), { weekday: "long" });
    const names = p.startDays
      .map((on: boolean, i: number) => (on ? weekday.format(new Date(2024, 0, 1 + i)) : ""))
      .filter((n: string) => n);
    const ListFormat = (Intl as any).ListFormat;
    const list = ListFormat ? new ListFormat(this.culture(), { type: "conjunction" }).format(names) : names.join(", ");
    return t("Starts on {0}").replace("{0}", list);
  }

  static completion(t: T, p: any): string {
    return p.completionDays ? t("{0} days to complete it").replace("{0}", p.completionDays) : "";
  }

  static columnWhen(t: T, p: any, col: any, i: number): string {
    if (p.layout === ByDays) {
      const bits: string[] = [];
      if (i > 0) bits.push(t("{0} later").replace("{0}", this.days(t, col.offsetMin || 1)));
      if (col.startFrom !== null && (col.startTo === null || col.startTo === col.startFrom)) {
        bits.push(t("Starts at {0}").replace("{0}", this.hour(col.startFrom)));
      } else if (col.startFrom !== null) {
        bits.push(t("Starts between {0} and {1}").replace("{0}", this.hour(col.startFrom)).replace("{1}", this.hour(col.startTo)));
      }
      return bits.join(" · ");
    }
    if (i === 0) return t("At the start");
    if (col.offsetMode === 2) return "";
    if (col.offsetMode === 1) {
      return t("Between {0} and {1} later").replace("{0}", this.days(t, col.offsetMin || 0)).replace("{1}", this.days(t, col.offsetMax || 0));
    }
    return col.offsetMin ? t("At least {0} later").replace("{0}", this.days(t, col.offsetMin)) : "";
  }

  static cardName(k: any): string {
    return k.quantity > 1 ? `${k.name} ×${k.quantity}` : k.name;
  }

  static cardDetail(t: T, k: any): string {
    if (k.cardType !== Booking) return "";
    const bits = [k.minutes + " " + t("min")];
    if (k.quantity > 1 && k.rhythmMode === 1) bits.push(t("1 per week"));
    if (k.quantity > 1 && k.rhythmMode === 2) bits.push(t("every {0} days").replace("{0}", k.rhythmDays));
    (k.otherMinutes || []).forEach((m: number) => bits.push(t("or {0} min").replace("{0}", String(m))));
    return bits.join(" · ");
  }

  static cards(p: any, type: number, optional: boolean = false): any[] {
    const out: any[] = [];
    p.columns.forEach((c: any) => c.cards.forEach((k: any) => {
      if (k.cardType === type && !!k.isOptional === optional) out.push(k);
    }));
    return out;
  }

  static itemNames(p: any): string[] {
    const names = this.cards(p, Product).map((k: any) => this.cardName(k));
    return names.filter((name: string, i: number) => names.indexOf(name) === i);
  }

  static sessions(p: any): number {
    return this.cards(p, Booking).reduce((n: number, k: any) => n + Math.max(1, k.quantity), 0);
  }

  static includedNames(t: T, p: any): string {
    const names: string[] = [];
    this.cards(p, Booking).concat(this.cards(p, Product)).forEach((k: any) => {
      const name = this.cardName(k);
      if (names.indexOf(name) < 0) names.push(name);
    });
    const shown = names.slice(0, 4).join(" · ");
    return names.length > 4 ? t("{0} and {1} more").replace("{0}", shown).replace("{1}", String(names.length - 4)) : shown;
  }

  static includedSummary(t: T, p: any): string {
    const n = p.columns.length;
    const bits = [this.count(t, this.sessions(p), "1 appointment", "{0} appointments")];
    bits.push(p.layout === ByDays ? this.count(t, n, "in one day", "in {0} days") : this.count(t, n, "1 phase", "{0} phases"));
    const items = this.cards(p, Product).length;
    if (items) bits.push(this.count(t, items, "1 item", "{0} items"));
    return bits.join(" · ");
  }

  static status(t: T, e: any): string {
    return t(["", "", "In progress", "Finished", "Cancelled", "Expired"][e.status] || "");
  }

  static done(e: any): number {
    return this.cards(e, Booking).reduce((n: number, k: any) => n + k.bookings.filter((b: any) => b.done).length, 0);
  }

  static enrollmentFacts(t: T, e: any): string[] {
    const facts: string[] = [];
    if (e.startDate) facts.push(t("Since {0}").replace("{0}", this.date(e.startDate)));
    if (e.expiresAt) facts.push(t("You have until {0}").replace("{0}", this.date(e.expiresAt)));
    facts.push(t("{0} of {1} appointments done").replace("{0}", String(this.done(e))).replace("{1}", String(this.sessions(e))));
    return facts;
  }

  // One line per session of a card: what was booked, or when the next one can be. A booked session still to come can
  // be cancelled; the next pending one of a program in progress can be booked from the bookings page. The pending ones
  // after it share a single line.
  static sessionRows(t: T, e: any, k: any): any[] {
    const total = Math.max(1, k.quantity);
    const rows: any[] = [];
    for (let i = 0; i < total; i++) {
      const booking = k.bookings[i];
      const next = i === k.bookings.length;
      const rest = !booking && !next && i < total - 1;
      const name = total === 1 ? k.name : rest ? `${k.name} ${i + 1}–${total}/${total}` : `${k.name} ${i + 1}/${total}`;
      rows.push({
        name: name,
        booked: !!booking,
        done: !!booking && !!booking.done,
        note: k.minutes ? k.minutes + " " + t("min") : "",
        detail: booking ? this.bookingText(t, booking) : this.pendingText(t, e, k, next),
        action: booking ? this.cancelAction(t, booking) : this.bookAction(t, e, k, next),
      });
      if (rest) break;
    }
    return rows;
  }

  static cancelAction(t: T, booking: any): any {
    return booking.cancelled || booking.done ? null : { label: t("Cancel"), bookingId: booking.id };
  }

  static bookAction(t: T, e: any, k: any, next: boolean): any {
    if (e.status !== InProgress || !next || !k.earliestDate) return null;
    const query = `card=${k.id}&minutes=${k.minutes || ""}&date=${k.earliestDate.substring(0, 10)}&back=/customer-programs/${e.id}`;
    return { label: t("Book"), to: `/bookings/${k.bookingTypeId}?${query}` };
  }

  static bookingText(t: T, booking: any): string {
    if (booking.done) return this.dateTime(booking.start);
    const state = booking.cancelled ? t("cancelled") : t("booked");
    return `${this.dateTime(booking.start)} · ${state}`;
  }

  static pendingText(t: T, e: any, k: any, next: boolean): string {
    if (e.status !== InProgress || !next) return t("not booked");
    return k.earliestDate ? t("bookable from {0}").replace("{0}", this.date(k.earliestDate, false)) : t("after the previous appointments");
  }
}
